import { forwardRef, type HTMLAttributes, type ImgHTMLAttributes, type ReactNode } from 'react';
import styled from 'styled-components';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import type { Document } from '@contentful/rich-text-types';
import { Spacing12 } from 'autogen/design-tokens/tokens';
import type { TypeImageBannerDetailed } from 'types/autogen/contentful';
import RichText from '../shared/RichText';
import WithAction from '../shared/WithAction';
import Image from './Image';
import ImageWrapper from './ImageWrapper';
import Picture from './Picture';

const Figure = styled.figure`
    display: flex;
    flex-direction: column;
    gap: ${Spacing12};
    margin-bottom: 0;
`;

const StyledPageAction = styled(WithAction)`
    &:hover {
        text-decoration: none;
    }
`;

const ImageRenderer = forwardRef<HTMLImageElement, TypeImageBannerDetailed & ImgHTMLAttributes<HTMLImageElement>>(
    (props, ref) => {
        const { sys, metadata, fields, ...restProps } = props;
        const { desktopImage, image } = fields;
        if (!image) return;

        if (image && desktopImage) {
            return <Picture ref={ref} image={image} desktopImage={desktopImage} {...restProps} />;
        }

        return <Image ref={ref} style={{ width: '100%' }} {...image} {...restProps} />;
    }
);

function WithCaption(props: {
    caption?: Document;
    inspectorProps?: ReturnType<typeof useContentfulInspectorMode>;
    children: ReactNode;
}) {
    const { caption, inspectorProps, children } = props;

    return caption ? (
        <Figure>
            {children}
            <figcaption {...inspectorProps?.({ fieldId: 'caption' })}>
                <RichText {...caption} />
            </figcaption>
        </Figure>
    ) : (
        children
    );
}

const ImageBanner = forwardRef((props: TypeImageBannerDetailed & HTMLAttributes<HTMLDivElement>, ref: any) => {
    const {
        fields,
        sys,
        metadata,
        sys: { id },
        ...restProps
    } = props;
    const { buttonText, buttonPosition, quickviewContent, theme, textOnImage, link, disableLazyload, caption } = fields;

    const inspectorProps = useContentfulInspectorMode({ entryId: id });
    const dialogOptions =
        buttonText && quickviewContent && buttonPosition
            ? {
                  buttonText: buttonText,
                  quickviewContent: quickviewContent,
                  buttonPosition: buttonPosition
              }
            : undefined;

    if (dialogOptions || theme || textOnImage) {
        return (
            <StyledPageAction link={link} style={restProps.style}>
                <WithCaption caption={caption} inspectorProps={inspectorProps}>
                    <ImageWrapper dialog={dialogOptions} theme={theme} textOnImage={textOnImage} id={id} {...restProps}>
                        <ImageRenderer
                            loading={disableLazyload ? 'eager' : 'lazy'}
                            {...props}
                            {...inspectorProps({ fieldId: 'image' })}
                        />
                    </ImageWrapper>
                </WithCaption>
            </StyledPageAction>
        );
    }

    return (
        <StyledPageAction link={link} style={restProps.style}>
            <WithCaption caption={caption} inspectorProps={inspectorProps}>
                <div {...restProps}>
                    <ImageRenderer
                        loading={disableLazyload ? 'eager' : 'lazy'}
                        {...props}
                        {...inspectorProps({ fieldId: 'image' })}
                    />
                </div>
            </WithCaption>
        </StyledPageAction>
    );
});

export default ImageBanner;
